<template>
  <div class="admin-view">
    <!-- 導覽列 -->
    <nav class="navbar">
      <div class="navbar-container">
        <div class="navbar-left">
          <img src="/logo.png" alt="SolarSDGs Logo" class="navbar-logo">
          <h1 class="navbar-title">{{ t('admin.title') }}</h1>
        </div>
        <div class="navbar-right">
          <span class="user-info">👤 {{ user?.customerName }}</span>
          <button @click="handleLogout" class="btn-logout">
            🚪 {{ t('common.logout') }}
          </button>
        </div>
      </div>
    </nav>

    <!-- 主要內容 -->
    <div class="admin-container">
      <!-- 頁籤切換 -->
      <div class="tabs">
        <button
          :class="['tab', { active: activeTab === 'devices' }]"
          @click="activeTab = 'devices'"
        >
          {{ t('admin.tabDevices') }}
        </button>
        <button
          :class="['tab', { active: activeTab === 'users' }]"
          @click="activeTab = 'users'"
        >
          {{ t('admin.tabUsers') }}
        </button>
      </div>

      <!-- 設備管理 -->
      <div v-if="activeTab === 'devices'" class="tab-content">
        <div class="section-header">
          <h2>{{ t('admin.deviceList') }}</h2>
          <button @click="showAddDeviceDialog" class="btn-add">
            {{ t('admin.addDevice') }}
          </button>
        </div>

        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>{{ t('admin.deviceId') }}</th>
                <th>{{ t('admin.deviceName') }}</th>
                <th>{{ t('admin.status') }}</th>
                <th>{{ t('admin.createdAt') }}</th>
                <th>{{ t('admin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="device in devices" :key="device.deviceId">
                <td>{{ device.deviceId }}</td>
                <td>{{ device.deviceName || '-' }}</td>
                <td>
                  <span :class="['status-badge', device.active ? 'active' : 'inactive']">
                    {{ device.active ? t('admin.enabled') : t('admin.disabled') }}
                  </span>
                </td>
                <td>{{ formatDate(device.createdAt) }}</td>
                <td>
                  <button @click="editDevice(device)" class="btn-icon" :title="t('common.edit')">
                    ✏️
                  </button>
                  <button @click="toggleDeviceStatus(device)" class="btn-icon" :title="device.active ? t('admin.disabled') : t('admin.enabled')">
                    {{ device.active ? '🔴' : '🟢' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 使用者管理 -->
      <div v-if="activeTab === 'users'" class="tab-content">
        <div class="section-header">
          <h2>{{ t('admin.userList') }}</h2>
          <button @click="showAddUserDialog" class="btn-add">
            {{ t('admin.addUser') }}
          </button>
        </div>

        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>{{ t('admin.account') }}</th>
                <th>{{ t('admin.name') }}</th>
                <th>{{ t('admin.accessibleDevices') }}</th>
                <th>{{ t('admin.status') }}</th>
                <th>{{ t('admin.createdAt') }}</th>
                <th>{{ t('admin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="customer in customers" :key="customer.id">
                <td>{{ customer.customerCode }}</td>
                <td>{{ customer.customerName }}</td>
                <td>
                  <span v-if="customer.devices === null" class="admin-badge">
                    {{ t('admin.allDevicesAdmin') }}
                  </span>
                  <span v-else class="device-list">
                    {{ customer.devices.join(', ') }}
                  </span>
                </td>
                <td>
                  <span :class="['status-badge', customer.active ? 'active' : 'inactive']">
                    {{ customer.active ? t('admin.enabled') : t('admin.disabled') }}
                  </span>
                </td>
                <td>{{ formatDate(customer.createdAt) }}</td>
                <td>
                  <button @click="editUser(customer)" class="btn-icon" :title="t('common.edit')">
                    ✏️
                  </button>
                  <button @click="assignDevices(customer)" class="btn-icon" :title="t('admin.assignDevicesTip')">
                    📡
                  </button>
                  <button @click="toggleUserStatus(customer)" class="btn-icon" :title="customer.active ? t('admin.disabled') : t('admin.enabled')">
                    {{ customer.active ? '🔴' : '🟢' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 新增/編輯設備對話框 -->
    <div v-if="showDeviceDialog" class="dialog-overlay">
      <div class="dialog" @click.stop>
        <div class="dialog-header">
          <h3>{{ editingDevice ? t('admin.editDevice') : t('admin.newDevice') }}</h3>
          <button type="button" @click="closeDeviceDialog" class="btn-close">✕</button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>{{ t('admin.deviceId') }} *</label>
            <input
              v-model="deviceForm.deviceId"
              type="text"
              :disabled="editingDevice !== null"
              :placeholder="t('admin.deviceIdPlaceholder')"
            >
          </div>
          <div class="form-group">
            <label>{{ t('admin.deviceName') }}</label>
            <input
              v-model="deviceForm.deviceName"
              type="text"
              :placeholder="t('admin.deviceNamePlaceholder')"
            >
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="deviceForm.active">
              {{ t('admin.enableDevice') }}
            </label>
          </div>
        </div>
        <div class="dialog-footer">
          <button type="button" @click="closeDeviceDialog" class="btn-cancel">{{ t('common.cancel') }}</button>
          <button type="button" @click="saveDevice" class="btn-save">{{ t('common.save') }}</button>
        </div>
      </div>
    </div>

    <!-- 新增/編輯使用者對話框 -->
    <div v-if="showUserDialog" class="dialog-overlay">
      <div class="dialog" @click.stop>
        <div class="dialog-header">
          <h3>{{ editingUser ? t('admin.editUser') : t('admin.newUser') }}</h3>
          <button type="button" @click="closeUserDialog" class="btn-close">✕</button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>{{ t('admin.account') }} *</label>
            <input
              v-model="userForm.customerCode"
              type="text"
              :disabled="editingUser !== null"
              :placeholder="t('admin.accountPlaceholder')"
            >
          </div>
          <div class="form-group">
            <label>{{ t('admin.name') }} *</label>
            <input
              v-model="userForm.customerName"
              type="text"
              :placeholder="t('admin.namePlaceholder')"
            >
          </div>
          <div class="form-group">
            <label>{{ t('admin.password') }} *</label>
            <input
              v-model="userForm.password"
              type="password"
              :placeholder="t('admin.passwordPlaceholder')"
            >
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="userForm.isAdmin">
              {{ t('admin.adminAccount') }}
            </label>
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="userForm.active">
              {{ t('admin.enableAccount') }}
            </label>
          </div>
        </div>
        <div class="dialog-footer">
          <button type="button" @click="closeUserDialog" class="btn-cancel">{{ t('common.cancel') }}</button>
          <button type="button" @click="saveUser" class="btn-save">{{ t('common.save') }}</button>
        </div>
      </div>
    </div>

    <!-- 分配設備對話框 -->
    <div v-if="showAssignDialog" class="dialog-overlay">
      <div class="dialog" @click.stop>
        <div class="dialog-header">
          <h3>{{ t('admin.assignTitle') }} - {{ assigningUser?.customerName }}</h3>
          <button type="button" @click="closeAssignDialog" class="btn-close">✕</button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="assignForm.isAdmin">
              {{ t('admin.setAdmin') }}
            </label>
          </div>
          <div v-if="!assignForm.isAdmin" class="device-checkboxes">
            <label v-for="device in devices" :key="device.deviceId" class="checkbox-item">
              <input
                type="checkbox"
                :value="device.deviceId"
                v-model="assignForm.selectedDevices"
              >
              {{ device.deviceId }} - {{ device.deviceName || t('admin.unnamedDevice') }}
            </label>
          </div>
        </div>
        <div class="dialog-footer">
          <button type="button" @click="closeAssignDialog" class="btn-cancel">{{ t('common.cancel') }}</button>
          <button type="button" @click="saveDeviceAssignment" class="btn-save">{{ t('common.save') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const { t } = useI18n()

// 用戶資訊
const user = ref<any>(null)

// 頁籤狀態
const activeTab = ref<'devices' | 'users'>('devices')

// 設備資料
const devices = ref<any[]>([])
const showDeviceDialog = ref(false)
const editingDevice = ref<any>(null)
const deviceForm = ref({
  deviceId: '',
  deviceName: '',
  active: true
})

// 使用者資料
const customers = ref<any[]>([])
const showUserDialog = ref(false)
const editingUser = ref<any>(null)
const userForm = ref({
  customerCode: '',
  customerName: '',
  password: '',
  isAdmin: false,
  active: true
})

// 分配設備
const showAssignDialog = ref(false)
const assigningUser = ref<any>(null)
const assignForm = ref({
  isAdmin: false,
  selectedDevices: [] as string[]
})

// 初始化
onMounted(async () => {
  const userStr = localStorage.getItem('user')
  if (userStr) {
    user.value = JSON.parse(userStr)
  }

  await loadDevices()
  await loadCustomers()
})

// 載入設備列表
async function loadDevices() {
  try {
    const token = localStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/devices`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (response.ok) {
      const result = await response.json()
      devices.value = result.data || []
    }
  } catch (error) {
    console.error('Failed to load devices:', error)
  }
}

// 載入使用者列表
async function loadCustomers() {
  try {
    const token = localStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/customers`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (response.ok) {
      const result = await response.json()
      customers.value = result.data || []
    }
  } catch (error) {
    console.error('Failed to load customers:', error)
  }
}

// 設備對話框
function showAddDeviceDialog() {
  editingDevice.value = null
  deviceForm.value = {
    deviceId: '',
    deviceName: '',
    active: true
  }
  showDeviceDialog.value = true
}

function editDevice(device: any) {
  editingDevice.value = device
  deviceForm.value = {
    deviceId: device.deviceId,
    deviceName: device.deviceName || '',
    active: device.active
  }
  showDeviceDialog.value = true
}

function closeDeviceDialog() {
  showDeviceDialog.value = false
  editingDevice.value = null
}

async function saveDevice() {
  try {
    const token = localStorage.getItem('token')
    const url = editingDevice.value
      ? `${import.meta.env.VITE_API_URL}/api/admin/devices/${deviceForm.value.deviceId}`
      : `${import.meta.env.VITE_API_URL}/api/admin/devices`

    const method = editingDevice.value ? 'PUT' : 'POST'

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(deviceForm.value)
    })

    if (response.ok) {
      alert(editingDevice.value ? t('admin.deviceUpdated') : t('admin.deviceCreated'))
      closeDeviceDialog()
      await loadDevices()
    } else {
      const result = await response.json()
      alert(t('admin.opFailed', { msg: result.error }))
    }
  } catch (error) {
    console.error('Failed to save device:', error)
    alert(t('admin.opFailedRetry'))
  }
}

async function toggleDeviceStatus(device: any) {
  if (!confirm(t('admin.confirmToggleDevice', { action: device.active ? t('admin.disable') : t('admin.enable'), id: device.deviceId }))) {
    return
  }

  try {
    const token = localStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/devices/${device.deviceId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...device,
        active: !device.active
      })
    })

    if (response.ok) {
      await loadDevices()
    }
  } catch (error) {
    console.error('Failed to toggle device status:', error)
  }
}

// 使用者對話框
function showAddUserDialog() {
  editingUser.value = null
  userForm.value = {
    customerCode: '',
    customerName: '',
    password: '',
    isAdmin: false,
    active: true
  }
  showUserDialog.value = true
}

function editUser(customer: any) {
  editingUser.value = customer
  userForm.value = {
    customerCode: customer.customerCode,
    customerName: customer.customerName,
    password: '',
    isAdmin: customer.devices === null,
    active: customer.active
  }
  showUserDialog.value = true
}

function closeUserDialog() {
  showUserDialog.value = false
  editingUser.value = null
}

async function saveUser() {
  try {
    const token = localStorage.getItem('token')
    const url = editingUser.value
      ? `${import.meta.env.VITE_API_URL}/api/admin/customers/${userForm.value.customerCode}`
      : `${import.meta.env.VITE_API_URL}/api/admin/customers`

    const method = editingUser.value ? 'PUT' : 'POST'

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userForm.value)
    })

    if (response.ok) {
      alert(editingUser.value ? t('admin.userUpdated') : t('admin.userCreated'))
      closeUserDialog()
      await loadCustomers()
    } else {
      const result = await response.json()
      alert(t('admin.opFailed', { msg: result.error }))
    }
  } catch (error) {
    console.error('Failed to save user:', error)
    alert(t('admin.opFailedRetry'))
  }
}

async function toggleUserStatus(customer: any) {
  if (!confirm(t('admin.confirmToggleUser', { action: customer.active ? t('admin.disable') : t('admin.enable'), id: customer.customerCode }))) {
    return
  }

  try {
    const token = localStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/customers/${customer.customerCode}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...customer,
        active: !customer.active
      })
    })

    if (response.ok) {
      await loadCustomers()
    }
  } catch (error) {
    console.error('Failed to toggle user status:', error)
  }
}

// 分配設備
function assignDevices(customer: any) {
  assigningUser.value = customer
  assignForm.value = {
    isAdmin: customer.devices === null,
    selectedDevices: customer.devices || []
  }
  showAssignDialog.value = true
}

function closeAssignDialog() {
  showAssignDialog.value = false
  assigningUser.value = null
}

async function saveDeviceAssignment() {
  try {
    const token = localStorage.getItem('token')
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/admin/customers/${assigningUser.value.customerCode}/devices`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isAdmin: assignForm.value.isAdmin,
          devices: assignForm.value.isAdmin ? null : assignForm.value.selectedDevices
        })
      }
    )

    if (response.ok) {
      alert(t('admin.assignSuccess'))
      closeAssignDialog()
      await loadCustomers()
    } else {
      const result = await response.json()
      alert(t('admin.opFailed', { msg: result.error }))
    }
  } catch (error) {
    console.error('Failed to assign devices:', error)
    alert(t('admin.opFailedRetry'))
  }
}

// 格式化日期
function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 登出
function handleLogout() {
  if (confirm(t('admin.confirmLogout'))) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('selectedDeviceId')
    router.push('/login')
  }
}
</script>

<style scoped>
.admin-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* 導覽列 */
.navbar {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 15px 0;
}

.navbar-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.navbar-logo {
  height: 40px;
  width: auto;
  border-radius: 8px;
  background-color: transparent;
}

.navbar-title {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.user-info {
  color: #666;
  font-size: 14px;
}

.btn-logout {
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-logout:hover {
  background: #f5f5f5;
  border-color: #d0d0d0;
}

/* 主要容器 */
.admin-container {
  max-width: 1400px;
  margin: 30px auto;
  padding: 0 20px;
}

/* 頁籤 */
.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tab {
  padding: 12px 24px;
  border: none;
  border-radius: 8px 8px 0 0;
  background: rgba(255, 255, 255, 0.7);
  color: #666;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
}

.tab:hover {
  background: rgba(255, 255, 255, 0.85);
}

.tab.active {
  background: white;
  color: #667eea;
  font-weight: bold;
}

/* 頁籤內容 */
.tab-content {
  background: white;
  border-radius: 0 8px 8px 8px;
  padding: 30px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h2 {
  margin: 0;
  color: #333;
}

.btn-add {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: #667eea;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-add:hover {
  background: #5568d3;
  transform: translateY(-2px);
}

/* 表格 */
.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.data-table th {
  background: #f5f5f5;
  font-weight: bold;
  color: #333;
}

.data-table tbody tr:hover {
  background: #f9f9f9;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.status-badge.active {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.inactive {
  background: #ffebee;
  color: #c62828;
}

.admin-badge {
  padding: 4px 12px;
  border-radius: 12px;
  background: #fff3e0;
  color: #e65100;
  font-size: 12px;
  font-weight: bold;
}

.device-list {
  color: #666;
  font-size: 13px;
}

.btn-icon {
  padding: 6px;
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn-icon:hover {
  transform: scale(1.2);
}

/* 對話框 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.dialog-header h3 {
  margin: 0;
  color: #333;
}

.btn-close {
  border: none;
  background: transparent;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.btn-close:hover {
  color: #333;
}

.dialog-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}

.form-group input[type="text"],
.form-group input[type="password"] {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
}

.form-group input[type="text"]:disabled {
  background: #f5f5f5;
  color: #999;
}

.form-group input[type="checkbox"] {
  margin-right: 8px;
}

.device-checkboxes {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
}

.checkbox-item {
  display: block;
  margin-bottom: 10px;
  cursor: pointer;
}

.checkbox-item input {
  margin-right: 8px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
}

.btn-cancel,
.btn-save {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-save {
  background: #667eea;
  color: white;
}

.btn-save:hover {
  background: #5568d3;
}
</style>
